import type { Lang } from '@/types/lang'
import { untitledLabel } from './untitled'

/**
 * Симметричный фолбэк локализованной пары полей: берёт значение своего языка,
 * а если оно пустое — значение другого.
 *
 * Почему симметричный, а не `en ?? ru`: базовые поля курса теперь могут быть
 * `null` (курс написан по-английски, весь текст в `*En`), и односторонний
 * фолбэк отдаёт русскому интерфейсу пустоту БЕЗ ошибки в консоли.
 *
 * Почему `trim`, а не `??`: перевод пишет пустую строку там, где модель ничего
 * не вернула, — `??` её пропускает как валидное значение.
 *
 * ТОЛЬКО ДЛЯ ПОКАЗА. В редактируемом поле фолбэк запрещён: автор сохранит
 * подставленный текст как перевод и затрёт признак непереведённости, по
 * которому бэк считает `pending_units` и берёт за перевод деньги.
 */
export const pickLang = (own?: string | null, other?: string | null): string =>
  own?.trim() ? own : other?.trim() ? other : ''

/**
 * Направленный симметричный фолбэк: берёт нужное поле по языку показа. Тонкая
 * обёртка над `pickLang`, вынесенная потому что `lang === 'ru' ? pickLang(a, b)
 * : pickLang(b, a)` было продублировано дословно в трёх местах.
 */
export const pickLangField = (
  ru: string | null | undefined,
  en: string | null | undefined,
  lang: Lang,
): string => (lang === 'ru' ? pickLang(ru, en) : pickLang(en, ru))

/** Пара полей названия курса — минимум, который нужен `courseDisplayName`. */
export interface NamedCourse {
  name: string | null
  nameEn: string | null
}

/**
 * Название курса для показа; пустое — «Без названия» на языке интерфейса
 * (`untitledLabel`). Обёртка существует потому, что эта строка была
 * продублирована дословно в пяти местах.
 */
export const courseDisplayName = (course: NamedCourse, lang: Lang): string =>
  pickLangField(course.name, course.nameEn, lang) || untitledLabel()

/** Есть ли у курса название хоть на одном языке — для серой подписи «Без названия». */
export const hasCourseName = (course: NamedCourse): boolean =>
  Boolean(course.name?.trim() || course.nameEn?.trim())
